import { UrlParams } from '../../enums/url-params'
import { useEssentials } from '../../hooks/utils/useEssentials'
import { Grid } from '@design-systems/canvas-ui'
import { Table } from 'components/table-compat/table-compat'
import { TimePeriods } from '../../components/timeperiods'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useOperationMetricsDetails } from '../../hooks/operations/useOperationMetricsDetails'
import {
    Bar,
    BarChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
    Cell as RechartsCell,
} from 'recharts'
import { BasicPlaceholder } from '../../components/placeholder'
import {
    createStoreMetricChartData,
    getFieldIndex,
    sortRowsBy,
    getCanvasColorFromHex,
    applyConditionalFormattingToRows,
    applyConditionalFormattingWithValueHiding,
    addClickableLinksToLinkedCells,
    computePreservedQuery,
    addHyperlinkToFirstColumnWithQueryBase,
    applyDrillBreadcrumbs,
    applyFooterAsTopRow,
    SUCCESS_HEX,
    TOTAL_PRIORITY_FILL,
    sortTimePeriods,
} from './helpers'
import type { DrillLevel } from './helpers'
import {
    buildDataTableObject,
    TableData,
    Cell as DataCell,
    Link,
} from '../../utils/data'
import { isStore } from '../../utils/location'
import { useNavigate } from 'react-router-dom'
import { ErrorAlert } from '../../components/error-fallback'

import { TRANSLATION_KEYS } from '../../locales/translation-keys'
import { translateTableData } from '../../locales/utils'
import { useOperationsContext } from './operations-context'

type ChartProps = {
    data: { metricData?: number; name?: string; color?: string }[]
    dataKey: string
    marginTop: number
    isStoreLoc: boolean
    yAxisTitle: string
    metricName?: string
}

export const OpsBarChart = memo(function OpsBarChart({
    data,
    dataKey,
    marginTop,
    isStoreLoc,
    yAxisTitle,
    metricName,
}: ChartProps) {
    const isPriorityFill = (metricName ?? '').trim() === TOTAL_PRIORITY_FILL

    return (
        <ResponsiveContainer width="100%" height={350}>
            <BarChart
                data={data}
                margin={{ bottom: isStoreLoc ? 90 : 20, top: marginTop }}
            >
                <XAxis
                    dataKey="name"
                    angle={isStoreLoc ? -25 : -90}
                    textAnchor="end"
                    interval={0}
                    height={isStoreLoc ? 60 : 100}
                    tickMargin={10}
                />
                <YAxis
                    dataKey="metricData"
                    label={{
                        value: yAxisTitle || '',
                        position: 'center',
                        angle: -90,
                        dx: -25,
                    }}
                />
                <Tooltip
                    cursor={false}
                    allowEscapeViewBox={{ x: true, y: true }}
                    content={(props: any) => {
                        const payloadItem = props?.payload?.[0]
                        const hovered = payloadItem?.payload
                        const color = (hovered?.color ?? '').toLowerCase()
                        const name = hovered?.name ?? ''
                        const value = payloadItem?.value as number | undefined

                        const isSuccessColor = color && SUCCESS_HEX.has(color)

                        if (isPriorityFill && isSuccessColor) return null

                        if (value == null) return null

                        // Positioning: flip to left if the hovered x coordinate is
                        // near the right edge of the chart
                        const coordX = props?.coordinate?.x ?? 0
                        const chartWidth =
                            props?.viewBox?.width ?? props?.chartWidth ?? 0
                        const flip = chartWidth && coordX > chartWidth - 60

                        return (
                            <div
                                className={`ops-chart-tooltip${flip ? ' ops-chart-tooltip--flipped' : ''}`}
                            >
                                {name && (
                                    <div className="ops-chart-tooltip__label">
                                        {name}
                                    </div>
                                )}
                                <div>{`${value}%`}</div>
                            </div>
                        )
                    }}
                />
                <Bar dataKey={dataKey}>
                    {data.map((entry, idx) => (
                        <RechartsCell
                            key={idx}
                            fill={getCanvasColorFromHex(entry.color)}
                        />
                    ))}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    )
})

export const OperationsMetricsDetails = memo(() => {
    const navigate = useNavigate()
    const { env, session, currentLocation, params, t } = useEssentials()
    const { activeTimePeriod, setActiveTimePeriod } = useOperationsContext()

    const isStoreLocation = isStore(currentLocation)

    const metricTimePeriods = params.get(UrlParams.TIME_PERIODS)

    const metricTimePeriodsArr = sortTimePeriods(
        (metricTimePeriods ?? '')
            .split(',')
            .map((p) => p.trim())
            .filter(Boolean),
    )

    const activeLabel =
        params.get(UrlParams.ACTIVE_TIME_PERIOD) ?? activeTimePeriod ?? ''

    // Pick initial by name; fallback to first; fallback to 'Prior Day'
    const initialTP = metricTimePeriodsArr.includes(activeLabel)
        ? activeLabel
        : (metricTimePeriodsArr[0] ?? 'Prior Day')

    const initialIdx = Math.max(0, metricTimePeriodsArr.indexOf(initialTP))

    const [timeDimension, setTimeDimension] = useState<string>(initialTP)
    const [viewIndex, setViewIndex] = useState<number>(initialIdx)
    const [detailRows, setDetailRows] = useState<DataCell[][]>([])
    const [exceptionRows, setExceptionRows] = useState<DataCell[][]>([])
    const [drillStack, setDrillStack] = useState<DrillLevel[]>([])

    const drillDownFilters = useMemo<
        { [key: string]: string } | undefined
    >(() => {
        if (!drillStack.length) return undefined
        const top = drillStack[drillStack.length - 1]
        return {
            drill_dimension: top.drill_dimension,
            drill_value: top.drill_value,
        }
    }, [drillStack])

    const isDrilledDown = drillStack.length > 0

    const metricName = params.get(UrlParams.METRIC_NAME)
    const rgdDetailCardId = params.get(UrlParams.RGD_DETAIL_CARD_ID) || ''
    const storeDetailCardId = params.get(UrlParams.STORE_DETAIL_CARD_ID) || ''
    const rgdExceptionCardId =
        params.get(UrlParams.RGD_EXCEPTION_CARD_ID) ?? undefined
    const storeExceptionCardId =
        params.get(UrlParams.STORE_EXCEPTION_CARD_ID) ?? undefined

    useEffect(() => {
        if (!metricName) {
            void navigate(`/operations?locationId=${currentLocation}`, {
                replace: true,
            })
        }
    }, [metricName, currentLocation, navigate])

    // --- URL sync helpers ------------------------------------------------------
    const updateUrlTimeSelection = useCallback(
        (timePeriod: string, idx: number) => {
            const next = new URLSearchParams(params)
            next.set(UrlParams.ACTIVE_TIME_PERIOD, timePeriod)
            next.set(UrlParams.ACTIVE_TIME_INDEX, String(idx))
            if (metricTimePeriodsArr.length) {
                next.set(UrlParams.TIME_PERIODS, metricTimePeriodsArr.join(','))
            }
            void navigate({ search: next.toString() }, { replace: true })
        },
        [navigate, params, metricTimePeriodsArr],
    )

    // Keep local state in sync when URL or list changes.
    // We intentionally exclude timeDimension and viewIndex from deps — including
    // them causes a re-sync loop because the effect itself updates those values.
    useEffect(() => {
        const label = params.get(UrlParams.ACTIVE_TIME_PERIOD) ?? ''
        const nextTP = metricTimePeriodsArr.includes(label)
            ? label
            : (metricTimePeriodsArr[0] ?? 'Prior Day')

        const nextIdx = Math.max(0, metricTimePeriodsArr.indexOf(nextTP))

        if (nextTP !== timeDimension || nextIdx !== viewIndex) {
            setTimeDimension(nextTP)
            setViewIndex(nextIdx)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params, metricTimePeriodsArr])

    const handleTimeDimensionChange = useCallback(
        (timePeriod: string, index: number) => {
            setTimeDimension(timePeriod)
            setViewIndex(index)
            updateUrlTimeSelection(timePeriod, index)
        },
        [updateUrlTimeSelection],
    )

    const handleTabSelect = useCallback(
        (timePeriod: string, index: number) => {
            handleTimeDimensionChange(timePeriod, index)
            setActiveTimePeriod(timePeriod)
            // Reset drill state when switching time periods
            setDrillStack([])
        },
        [handleTimeDimensionChange, setActiveTimePeriod],
    )

    const {
        data: detailCardData,
        isLoading: detailCardLoading,
        isError: isDetailCardCallError,
        error: detailCardCallError,
        refetch: detailCardRefetch,
    } = useOperationMetricsDetails({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
        card: isStoreLocation ? storeDetailCardId : rgdDetailCardId,
        timeDimension,
        filters: drillDownFilters,
    })

    const {
        data: exceptionCardData,
        isLoading: exceptionCardLoading,
        isError: isExceptionCardCallError,
        error: exceptionCardError,
        refetch: exceptionCardRefetch,
    } = useOperationMetricsDetails({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
        card: isStoreLocation ? storeExceptionCardId : rgdExceptionCardId,
        timeDimension,
        filters: drillDownFilters,
    })

    useEffect(() => {
        setDetailRows(detailCardData?.rows ?? [])
    }, [detailCardData])

    useEffect(() => {
        setExceptionRows(exceptionCardData?.rows ?? [])
    }, [exceptionCardData])

    // Save the current footer so we can capture it before the next
    // drill replaces the data
    const currentFooterRef = useRef<DataCell[]>([])
    useEffect(() => {
        if (detailCardData?.footer) {
            currentFooterRef.current = detailCardData.footer
        }
    }, [detailCardData?.footer])

    const handleCellClick = useCallback(
        (value: string, link: Link) => {
            if (isStoreLocation && link?.drill) {
                const drill = link.drill
                setDrillStack((prev) => {
                    const label =
                        prev.length === 0
                            ? currentLocation
                            : prev[prev.length - 1].drill_value
                    return [
                        ...prev,
                        {
                            label,
                            footer: currentFooterRef.current,
                            drill_dimension: drill,
                            drill_value: value,
                        },
                    ]
                })
            }
        },
        [isStoreLocation, currentLocation],
    )

    const handleDrillBack = useCallback((level: number) => {
        setDrillStack((prev) => prev.slice(0, level))
    }, [])

    const onCellDataClick = useCallback(
        (data: { value: string; id?: string }) => {
            if (!data.id) return

            try {
                const parsed = JSON.parse(decodeURIComponent(data.id))

                // Handle breadcrumb click (drill back to a specific level)
                if (parsed.drillBack) {
                    handleDrillBack(parsed.level ?? 0)
                    return
                }

                const { drill, value: drillValue } = parsed
                handleCellClick(drillValue, { drill, href: '#' })
            } catch {
                return
            }
        },
        [handleCellClick, handleDrillBack],
    )

    const onDetailSort = useCallback(
        (sortDirection: 'asc' | 'desc' | 'dsc', field: string) => {
            if (!detailRows.length || !detailCardData?.headings) return
            const idx = getFieldIndex(detailCardData.headings, field)
            if (idx < 0) return
            setDetailRows((prev) => sortRowsBy(prev, idx, sortDirection))
        },
        [detailCardData?.headings, detailRows.length],
    )

    const onExceptionSort = useCallback(
        (sortDirection: 'asc' | 'desc' | 'dsc', field: string) => {
            if (!exceptionRows.length || !exceptionCardData?.headings) return
            const idx = getFieldIndex(exceptionCardData.headings, field)
            if (idx < 0) return
            setExceptionRows((prev) => sortRowsBy(prev, idx, sortDirection))
        },
        [exceptionCardData?.headings, exceptionRows.length],
    )

    const preservedQuery = useMemo(
        () =>
            computePreservedQuery(
                metricTimePeriodsArr,
                timeDimension,
                viewIndex,
            ),
        [metricTimePeriodsArr, timeDimension, viewIndex],
    )

    const addHyperlinkToFirstColumnWithQuery = useCallback(
        (table: TableData, loc: string) =>
            addHyperlinkToFirstColumnWithQueryBase(table, loc, preservedQuery),
        [preservedQuery],
    )

    const detailTableData: TableData = useMemo<TableData>(() => {
        const headings = detailCardData?.headings ? detailCardData.headings : []
        const formattedDetailCardRows =
            applyConditionalFormattingWithValueHiding(
                detailRows ?? [],
                metricName,
                headings,
            )
        const detailTable: TableData = {
            rows: formattedDetailCardRows,
            headings,
            footer: detailCardData?.footer ?? [],
        }

        // Translate table headings
        const translatedTable = translateTableData({
            t,
            table: detailTable,
            translation: TRANSLATION_KEYS.operationalMetrics.detailsColumns,
        })

        const tableWithFirstColumnLinks = !isStoreLocation
            ? addHyperlinkToFirstColumnWithQuery(
                  translatedTable,
                  currentLocation,
              )
            : translatedTable

        const withLinks = addClickableLinksToLinkedCells(
            tableWithFirstColumnLinks,
            isStoreLocation,
        )

        // When drilled in, add breadcrumb rows and move footer to top
        if (isDrilledDown) {
            return applyDrillBreadcrumbs(withLinks, drillStack, headings.length)
        }

        // At store level (not drilled), show footer as top row
        if (isStoreLocation) {
            return applyFooterAsTopRow(
                withLinks,
                currentLocation,
                headings.length,
            )
        }

        return withLinks
    }, [
        detailRows,
        detailCardData,
        isStoreLocation,
        currentLocation,
        addHyperlinkToFirstColumnWithQuery,
        metricName,
        t,
        isDrilledDown,
        drillStack,
    ])

    const exceptionTableData: TableData = useMemo<TableData>(() => {
        const headings = exceptionCardData?.headings
            ? exceptionCardData.headings
            : []
        const formattedExceptionRows = applyConditionalFormattingToRows(
            exceptionRows ?? [],
        )
        const exceptionsTable: TableData = {
            rows: formattedExceptionRows,
            headings,
            footer: exceptionCardData?.footer ?? [],
        }

        // Translate table headings
        const translatedTable = translateTableData({
            t,
            table: exceptionsTable,
            translation: TRANSLATION_KEYS.operationalMetrics.detailsColumns,
        })

        const tableWithFirstColumnLinks = !isStoreLocation
            ? addHyperlinkToFirstColumnWithQuery(
                  translatedTable,
                  currentLocation,
              )
            : translatedTable

        const withLinks = addClickableLinksToLinkedCells(
            tableWithFirstColumnLinks,
            isStoreLocation,
        )

        // When drilled in, add breadcrumb rows and move footer to top
        if (isDrilledDown) {
            return applyDrillBreadcrumbs(withLinks, drillStack, headings.length)
        }

        // At store level (not drilled), show footer as top row
        if (isStoreLocation) {
            return applyFooterAsTopRow(
                withLinks,
                currentLocation,
                headings.length,
            )
        }

        return withLinks
    }, [
        exceptionRows,
        exceptionCardData,
        isStoreLocation,
        currentLocation,
        addHyperlinkToFirstColumnWithQuery,
        t,
        isDrilledDown,
        drillStack,
    ])

    const tableOpsMetricDetailData = useMemo(() => {
        if (!detailCardLoading && detailCardData) {
            return buildDataTableObject({
                currentData: detailTableData,
                widths: [100, 100, 100, 100, 100, 100, 100, 100, 100],
                pinFirstColumn: true,
                sortable: true,
                isLoading: detailCardLoading,
                onSort: onDetailSort,
                handleDataClick: onCellDataClick,
            })
        }
    }, [
        detailCardData,
        detailCardLoading,
        detailTableData,
        onDetailSort,
        onCellDataClick,
    ])

    const tableOpsMetricExceptionData = useMemo(() => {
        if (!exceptionCardLoading && exceptionCardData) {
            return buildDataTableObject({
                currentData: exceptionTableData,
                widths: [100, 100, 100, 100, 100, 100, 100, 100, 100],
                pinFirstColumn: true,
                sortable: true,
                isLoading: exceptionCardLoading,
                onSort: onExceptionSort,
                handleDataClick: onCellDataClick,
            })
        }
    }, [
        exceptionCardData,
        exceptionCardLoading,
        exceptionTableData,
        onExceptionSort,
        onCellDataClick,
    ])

    const chartData = useMemo(
        () =>
            (
                createStoreMetricChartData(detailCardData?.rows)
                    .storeOpsMetricData ?? []
            ).map((d) => ({ ...d, metricData: Number(d.metricData) })),
        [detailCardData?.rows],
    )
    const yAxisTitle = useMemo(
        () =>
            detailCardData?.headings?.[detailCardData.headings.length - 1]
                ?.value ?? '',
        [detailCardData?.headings],
    )

    const hasChartData = useMemo(() => {
        return (
            chartData.length > 0 &&
            chartData.some(
                (d) =>
                    !!d.name &&
                    typeof d.metricData === 'number' &&
                    Number.isFinite(d.metricData),
            )
        )
    }, [chartData])

    if (detailCardLoading) {
        return (
            <div
                className="placeholder-container hc8-pt-6x"
                data-testid="placeholder"
            >
                <BasicPlaceholder type="table" rows={5} columns={5} />
            </div>
        )
    }

    // Only return error for detail card since it's the primary data
    // Exception card errors will be handled inline
    if (isDetailCardCallError) {
        return (
            <ErrorAlert
                error={detailCardCallError}
                onRetry={() => detailCardRefetch()}
            />
        )
    }

    const isStoreLoc = isStore(currentLocation)

    return (
        <>
            <TimePeriods
                timePeriods={metricTimePeriodsArr}
                activeTimePeriod={timeDimension}
                onTabSelect={handleTabSelect}
                isTimePeriodSticky={false}
                page="operations details"
            />
            <Grid.Container className="hc8-mt-4x">
                {hasChartData && (
                    <Grid.Item
                        className="hc8-ph-4x hc8-pv-none"
                        data-testid="ops-chart"
                        size={{
                            xs: 12,
                        }}
                    >
                        <OpsBarChart
                            data={chartData}
                            dataKey="metricData"
                            marginTop={40}
                            isStoreLoc={isStoreLoc}
                            yAxisTitle={yAxisTitle}
                            metricName={metricName ?? undefined}
                        />
                    </Grid.Item>
                )}
                <Grid.Item
                    className="hc8-ph-4x hc8-pt-none"
                    size={{
                        xs: 12,
                    }}
                >
                    <Table
                        data={tableOpsMetricDetailData}
                        scrollableWidth={true}
                    />
                </Grid.Item>
                {(rgdExceptionCardId || storeExceptionCardId) && (
                    <Grid.Item
                        className="hc8-ph-4x"
                        size={{
                            xs: 12,
                        }}
                    >
                        {exceptionCardLoading ? (
                            <div
                                className="placeholder-container"
                                data-testid="placeholder-exceptions"
                            >
                                <BasicPlaceholder
                                    type="table"
                                    rows={5}
                                    columns={5}
                                />
                            </div>
                        ) : isExceptionCardCallError ? (
                            <ErrorAlert
                                error={exceptionCardError}
                                onRetry={() => exceptionCardRefetch()}
                            />
                        ) : (
                            exceptionCardData &&
                            tableOpsMetricExceptionData && (
                                <Table
                                    data={tableOpsMetricExceptionData}
                                    scrollableWidth={true}
                                />
                            )
                        )}
                    </Grid.Item>
                )}
            </Grid.Container>
        </>
    )
})
